import React from "react";
import { Link } from "react-router-dom";

import { useWhiteLabel } from "../../context/WhiteLabel";

// Páginas públicas (sem login) de Privacidade e Termos de Uso. São o endereço
// que a App Store e a Play Store exigem para o app, e valem também para o
// painel web.
//
// Os dados da empresa responsável ficam em `EMPRESA`. O que estiver em branco
// simplesmente não aparece na página — mas a política só está completa com o
// e-mail do encarregado preenchido.
const EMPRESA = {
  razaoSocial: "NP Soluções em TI Ltda",
  cnpj: "35.095.089/0001-00",
  endereco: "",
  emailPrivacidade: "",
  atualizadoEm: "11 de outubro de 2026",
};

const Pagina = ({ titulo, children }) => {
  const { appName } = useWhiteLabel();
  const nome = appName || "NP Connect";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <span className="text-base font-semibold">{nome}</span>
          <nav className="flex gap-4 text-sm text-muted-foreground">
            <Link to="/privacidade" className="hover:text-foreground">
              Privacidade
            </Link>
            <Link to="/termos" className="hover:text-foreground">
              Termos de uso
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 leading-relaxed">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{titulo}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Atualizada em {EMPRESA.atualizadoEm}
          </p>
        </div>
        {children}
      </main>
    </div>
  );
};

const Secao = ({ titulo, children }) => (
  <section className="space-y-2">
    <h2 className="text-lg font-semibold">{titulo}</h2>
    <div className="space-y-2 text-sm text-muted-foreground [&_strong]:text-foreground">
      {children}
    </div>
  </section>
);

const Lista = ({ itens }) => (
  <ul className="list-disc space-y-1 pl-5">
    {itens.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

const Responsavel = () => {
  const partes = [EMPRESA.razaoSocial, EMPRESA.cnpj && `CNPJ ${EMPRESA.cnpj}`, EMPRESA.endereco].filter(Boolean);

  return partes.length ? <p>{partes.join(" · ")}</p> : null;
};

export const Privacidade = () => (
  <Pagina titulo="Política de Privacidade">
    <Secao titulo="1. Quem somos e quais são os papéis">
      <p>
        O NP Connect é uma plataforma de atendimento ao cliente por mensagens
        (WhatsApp e outros canais), oferecida a empresas. No painel web e no
        aplicativo, quem usa o sistema são os atendentes e administradores da
        empresa contratante.
      </p>
      <Responsavel />
      <p>
        <strong>A empresa contratante</strong> decide por que e como os dados
        de seus clientes são tratados nos atendimentos (é a controladora desse
        conteúdo). <strong>Nós</strong> tratamos esse conteúdo em nome dela
        (operadora) e somos controladores dos dados de conta e de segurança do
        sistema, descritos abaixo.
      </p>
    </Secao>

    <Secao titulo="2. Dados que tratamos">
      <Lista
        itens={[
          "Conta de quem usa o sistema: nome, e-mail, cargo e permissões, filas e preferências.",
          "Conteúdo dos atendimentos: mensagens, imagens, vídeos, áudios, documentos, nome e telefone dos contatos atendidos, e a localização, quando o atendente escolhe enviá-la numa conversa.",
          "Dados técnicos: identificador do aparelho para notificações (token), registros de acesso e de uso (endereço IP, data e hora, erros) e dados de sessão.",
          "Preferências do aplicativo (tema, assinatura de mensagens, biometria), guardadas no próprio aparelho.",
        ]}
      />
      <p>
        <strong>Não vendemos dados, não fazemos publicidade e não rastreamos
        você entre aplicativos ou sites de terceiros.</strong> O login
        biométrico (Face ID ou digital) é feito pelo sistema do aparelho; não
        recebemos nem guardamos sua biometria.
      </p>
    </Secao>

    <Secao titulo="3. Para que usamos os dados">
      <Lista
        itens={[
          "Prestar o serviço de atendimento: entregar e receber mensagens, organizar filas e atendimentos, histórico e relatórios.",
          "Enviar notificações de novas mensagens ao aparelho do atendente.",
          "Manter a segurança, prevenir fraude e abuso, e investigar falhas.",
          "Cumprir obrigações legais e regulatórias.",
          "Quando o atendente aciona um recurso de inteligência artificial (sugerir resposta, corrigir ou traduzir texto, transcrever áudio), o texto ou o áudio necessário é enviado ao provedor de IA configurado pela empresa, apenas para gerar aquele resultado.",
        ]}
      />
      <p>
        As bases legais (LGPD, art. 7º) são a execução do contrato com a
        empresa contratante, o cumprimento de obrigação legal e o legítimo
        interesse em manter o serviço seguro.
      </p>
    </Secao>

    <Secao titulo="4. Com quem compartilhamos">
      <Lista
        itens={[
          "Provedores de infraestrutura e hospedagem do servidor.",
          "Google (Firebase Cloud Messaging) e Apple (APNs), para entregar notificações push ao aparelho. Eles recebem o token do aparelho e o aviso a ser exibido.",
          "WhatsApp/Meta e demais canais conectados pela empresa, pelos quais as mensagens trafegam.",
          "Provedores de IA, nas condições do item 3.",
          "Autoridades, quando a lei exigir.",
        ]}
      />
      <p>Não compartilhamos dados com terceiros para fins de marketing.</p>
    </Secao>

    <Secao titulo="5. Por quanto tempo guardamos">
      <p>
        Mantemos os dados enquanto o contrato da empresa estiver ativo e pelo
        tempo necessário para cumprir obrigações legais ou resolver
        disputas. A empresa contratante pode pedir a exclusão de dados do
        atendimento; registros de segurança são mantidos pelo prazo legal.
      </p>
    </Secao>

    <Secao titulo="6. Segurança">
      <p>
        Usamos conexão criptografada (HTTPS/TLS), controle de acesso por
        perfil e permissão, tokens de sessão de curta duração e armazenamento
        seguro das credenciais no aparelho. Nenhum sistema é totalmente
        imune a falhas; em caso de incidente relevante, comunicaremos a
        empresa contratante e a autoridade competente nos termos da lei.
      </p>
    </Secao>

    <Secao titulo="7. Seus direitos">
      <p>
        Nos termos da LGPD (art. 18), você pode pedir confirmação de
        tratamento, acesso, correção, anonimização, portabilidade, informação
        sobre compartilhamento e a eliminação de dados tratados com seu
        consentimento, além de revogar o consentimento.
      </p>
      <p>
        {EMPRESA.emailPrivacidade ? (
          <>
            Para exercer seus direitos, escreva para{" "}
            <a className="underline" href={`mailto:${EMPRESA.emailPrivacidade}`}>
              {EMPRESA.emailPrivacidade}
            </a>
            .
          </>
        ) : (
          "Para exercer seus direitos, fale com o administrador da sua empresa."
        )}{" "}
        Se você é cliente final de uma empresa que usa o NP Connect, o pedido
        sobre o conteúdo do seu atendimento deve ser feito à própria empresa.
      </p>
    </Secao>

    <Secao titulo="8. Contas e exclusão">
      <p>
        As contas são criadas pelo administrador da empresa; o aplicativo não
        permite cadastro aberto. Para encerrar ou excluir uma conta, peça ao
        administrador da sua empresa ou ao contato acima.
      </p>
    </Secao>

    <Secao titulo="9. Crianças e adolescentes">
      <p>O serviço é destinado a uso profissional, por maiores de 18 anos.</p>
    </Secao>

    <Secao titulo="10. Mudanças nesta política">
      <p>
        Podemos atualizar esta política. A data no topo indica a versão
        vigente; mudanças relevantes serão comunicadas no sistema.
      </p>
    </Secao>
  </Pagina>
);

export const Termos = () => (
  <Pagina titulo="Termos de Uso">
    <Secao titulo="1. Aceitação">
      <p>
        Ao entrar no NP Connect (painel web ou aplicativo), você concorda com
        estes termos e com a Política de Privacidade. Se não concordar, não
        use o serviço.
      </p>
      <Responsavel />
    </Secao>

    <Secao titulo="2. O serviço">
      <p>
        O NP Connect permite que empresas organizem e respondam atendimentos
        recebidos por WhatsApp e outros canais, com filas, equipes, histórico,
        notificações e recursos opcionais de inteligência artificial e
        ligações.
      </p>
      <p>
        <strong>O NP Connect é um produto independente. Não é afiliado,
        patrocinado nem endossado pelo WhatsApp LLC ou pela Meta.</strong>{" "}
        WhatsApp é marca de seus titulares.
      </p>
    </Secao>

    <Secao titulo="3. Contas e acesso">
      <p>
        As contas são criadas pelo administrador da empresa contratante. Você
        é responsável por manter sua senha em sigilo e por tudo o que for
        feito com a sua conta. Avise o administrador se suspeitar de uso
        indevido.
      </p>
    </Secao>

    <Secao titulo="4. Uso permitido">
      <p>Você se compromete a:</p>
      <Lista
        itens={[
          "usar o serviço apenas para atendimento legítimo, em conformidade com a lei e com os termos dos canais conectados (incluindo as políticas do WhatsApp);",
          "não enviar spam, mensagens em massa não solicitadas, conteúdo ilegal, ofensivo ou que viole direitos de terceiros;",
          "não tentar acessar áreas ou dados sem permissão, nem interferir no funcionamento do serviço;",
          "ter base legal (como consentimento ou legítimo interesse) para tratar os dados dos clientes que atende.",
        ]}
      />
    </Secao>

    <Secao titulo="5. Conteúdo e responsabilidade da empresa">
      <p>
        O conteúdo dos atendimentos pertence à empresa contratante e a seus
        clientes. A empresa é responsável pelo que seus atendentes enviam e
        pela licitude do tratamento dos dados de seus clientes.
      </p>
    </Secao>

    <Secao titulo="6. Inteligência artificial">
      <p>
        Os recursos de IA geram sugestões, correções, traduções e
        transcrições automáticas, que podem conter erros. Nada é enviado
        sozinho: revise o resultado antes de usar. Não inclua informações
        sensíveis desnecessárias nos textos enviados à IA.
      </p>
    </Secao>

    <Secao titulo="7. Disponibilidade">
      <p>
        Buscamos manter o serviço disponível, mas ele depende de redes e de
        terceiros (como WhatsApp, Apple e Google) e pode sofrer interrupções,
        manutenção ou mudanças fora do nosso controle.
      </p>
    </Secao>

    <Secao titulo="8. Propriedade intelectual">
      <p>
        O software, a marca e o conteúdo do NP Connect pertencem aos seus
        titulares. Você recebe um direito de uso limitado, não exclusivo e
        intransferível, enquanto durar o contrato da empresa.
      </p>
    </Secao>

    <Secao titulo="9. Limitação de responsabilidade">
      <p>
        Nos limites da lei, não respondemos por danos indiretos, lucros
        cessantes ou perdas decorrentes de indisponibilidade de canais de
        terceiros, uso indevido por usuários ou conteúdo enviado pela empresa
        ou por seus clientes.
      </p>
    </Secao>

    <Secao titulo="10. Suspensão e encerramento">
      <p>
        Podemos suspender ou encerrar o acesso em caso de violação destes
        termos, risco à segurança ou fim do contrato da empresa.
      </p>
    </Secao>

    <Secao titulo="11. Mudanças, lei aplicável e contato">
      <p>
        Podemos atualizar estes termos; a data no topo indica a versão
        vigente. Aplica-se a lei brasileira, e fica eleito o foro da comarca
        da sede da empresa responsável.
      </p>
      {EMPRESA.emailPrivacidade ? (
        <p>
          Dúvidas:{" "}
          <a className="underline" href={`mailto:${EMPRESA.emailPrivacidade}`}>
            {EMPRESA.emailPrivacidade}
          </a>
          .
        </p>
      ) : null}
    </Secao>
  </Pagina>
);
